import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Reveal, Kicker } from "@/components/motion";
import { ProjectCard } from "@/components/ProjectCard";
import { ArticleCard } from "@/pages/NovaTV";
import { api } from "@/lib/api";

export const FeaturedProjects = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then((r) => setProjects(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (projects.length === 0) return null;
  return (
    <section className="bg-[#F8FAFC] py-24 md:py-32" data-testid="home-featured-projects">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>{t("home2.projKicker")}</Kicker>
            <h2 className="mt-4 font-display text-3xl tracking-tight text-[#111827] md:text-5xl">{t("home2.projTitle")}</h2>
          </div>
          <Link to="/projets" data-testid="home-all-projects-link" className="inline-flex items-center gap-2 rounded-xl border border-[#111827]/15 px-6 py-3 text-sm font-semibold text-[#111827] transition-colors duration-300 hover:border-[#2E8BFF] hover:text-[#2E8BFF]">
            {t("home2.projAll")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div
          className={`mt-12 grid gap-5 ${
            projects.length >= 3 ? "md:grid-cols-3" : projects.length === 2 ? "md:grid-cols-2 lg:max-w-4xl" : "md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {projects.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1} className={`h-full ${projects.length === 1 ? "md:col-span-2 lg:col-span-2" : ""}`}>
              <ProjectCard project={p} flagship={p.slug === "mont-mascal"} wide={projects.length === 1} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const EcosystemBanner = () => {
  const { t } = useTranslation();
  return (
    <section className="grain relative overflow-hidden bg-[#111827] py-20" data-testid="home-ecosystem-banner">
      <div className="pointer-events-none absolute right-[-80px] top-1/2 hidden -translate-y-1/2 lg:block">
        <div className="orbit-ring h-72 w-72 rounded-full border border-dashed border-white/15" />
        <div className="orbit-ring-reverse absolute inset-10 rounded-full border border-dashed border-[#4DB8FF]/30" />
        {[0, 72, 144, 216, 288].map((deg) => (
          <span key={deg} className="absolute left-1/2 top-1/2 h-2.5 w-2.5 rounded-full bg-[#4DB8FF]" style={{ transform: `rotate(${deg}deg) translateX(144px)` }} />
        ))}
      </div>
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Kicker light>{t("home2.ecoKicker")}</Kicker>
          <h2 className="mt-4 max-w-xl font-display text-3xl text-white md:text-4xl">{t("home2.ecoTitle")}</h2>
          <Link
            to="/private-network#ecosysteme"
            data-testid="home-ecosystem-link"
            className="mt-8 inline-flex items-center gap-2 rounded-xl shadow-sm bg-white px-7 py-3.5 text-sm font-semibold text-[#111827] transition-transform duration-300 hover:scale-105"
          >
            {t("home2.ecoCta")}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/private-network"
            data-testid="home-pn-link"
            className="mt-4 block text-sm font-medium text-[#7CC4FF] transition-colors hover:text-white"
          >
            {t("pn.homeLink")} →
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export const LatestArticles = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/novatv").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (articles.length === 0) return null;
  return (
    <section className="bg-white py-24 md:py-32" data-testid="home-latest-articles">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>{t("home2.tvKicker")}</Kicker>
            <h2 className="mt-4 font-display text-3xl tracking-tight text-[#111827] md:text-5xl">{t("home2.tvTitle")}</h2>
          </div>
          <Link to="/novatv" data-testid="home-all-articles-link" className="inline-flex items-center gap-2 rounded-xl border border-[#111827]/15 px-6 py-3 text-sm font-semibold text-[#111827] transition-colors duration-300 hover:border-[#2E8BFF] hover:text-[#2E8BFF]">
            {t("home2.tvAll")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.1} className="h-full">
              <ArticleCard article={a} testid={`home-article-${a.slug}`} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
